import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { DataTable, ListPageLayout, type DataTableColumn } from '@/shared/components/entities'
import { OverflowMenu, type OverflowMenuItem } from '@/shared/components/ui/OverflowMenu'
import { useTranslation } from '@/shared/contexts/TranslationContext'
import { useUnit } from '@/modules/units/contexts/UnitContext'
import { listAccountingClients } from '@/modules/accounting/clients/accountingClientsService'
import type { AccountingClient } from '@/modules/accounting/clients/accountingClientTypes'
import {
  FiscalMemoryImportModal,
} from '@/modules/accounting/memory/FiscalMemoryImportModal'
import { FiscalMemoryXmlLinkModal } from '@/modules/accounting/memory/FiscalMemoryXmlLinkModal'
import { FiscalMemoryDetailsModal } from '@/modules/accounting/memory/FiscalMemoryDetailsModal'
import { FiscalMemoryClearModal } from '@/modules/accounting/memory/FiscalMemoryClearModal'
import {
  listAccountingProducts,
  listAvailableSpedDocuments,
  listFiscalMemoryImports,
} from '@/modules/accounting/memory/accountingFiscalMemoryService'
import type {
  AccountingProduct,
  FiscalMemoryImport,
} from '@/modules/accounting/memory/accountingFiscalMemoryTypes'
import type { StoredDocument } from '@/modules/documents'

function MemoryIcon() {
  return <svg className="h-7 w-7 text-emerald-700 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 3.75h6M9 20.25h6M7.5 6.75h9a2.25 2.25 0 0 1 2.25 2.25v6A2.25 2.25 0 0 1 16.5 17.25h-9A2.25 2.25 0 0 1 5.25 15V9A2.25 2.25 0 0 1 7.5 6.75Zm1.5 3h6m-6 3h4.5" /></svg>
}

interface FiscalMemoryPeriodSummary {
  key: string
  period_start: string | null
  period_end: string | null
  imports: FiscalMemoryImport[]
  primary: FiscalMemoryImport
  fiscal: FiscalMemoryImport | null
  contributions: FiscalMemoryImport | null
  product_count: number
  establishment_activity_kind: FiscalMemoryImport['establishment_activity_kind']
  xml_enrichment_status: FiscalMemoryImport['xml_enrichment_status']
  xml_expected_count: number
  xml_matched_count: number
  xml_missing_count: number
}

export function FiscalMemoryPage() {
  const { partyId } = useParams<{ partyId: string }>()
  const navigate = useNavigate()
  const { t, locale } = useTranslation()
  const { currentUnit } = useUnit()
  const [client, setClient] = useState<AccountingClient | null>(null)
  const [products, setProducts] = useState<AccountingProduct[]>([])
  const [imports, setImports] = useState<FiscalMemoryImport[]>([])
  const [documents, setDocuments] = useState<StoredDocument[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [viewMode, setViewMode] = useState<'card' | 'table'>('card')
  const [memoryView, setMemoryView] = useState<'individual' | 'summary'>('individual')
  const [showImport, setShowImport] = useState(false)
  const [xmlImports, setXmlImports] = useState<FiscalMemoryImport[]>([])
  const [selectedProduct, setSelectedProduct] = useState<AccountingProduct | null>(null)
  const [showClearMemory, setShowClearMemory] = useState(false)
  const [clearPeriodImport, setClearPeriodImport] = useState<FiscalMemoryImport | null>(null)

  const load = useCallback(async () => {
    if (!currentUnit?.id || !partyId) return
    setLoading(true)
    setError(null)
    try {
      const clients = await listAccountingClients(currentUnit.id)
      const selected = clients.find((candidate) => candidate.party_id === partyId)
      if (!selected) throw new Error('accounting_client_not_found')
      setClient(selected)
      const [loadedProducts, loadedImports, loadedDocuments] = await Promise.all([
        listAccountingProducts(selected.client_unit_id),
        listFiscalMemoryImports(selected.client_unit_id),
        listAvailableSpedDocuments(selected.client_unit_id),
      ])
      setProducts(loadedProducts)
      setImports(loadedImports)
      setDocuments(loadedDocuments)
    } catch (loadError) {
      console.error('[FiscalMemoryPage] Load error:', loadError)
      setError(t('accounting.fiscalMemory.errors.load'))
    } finally {
      setLoading(false)
    }
  }, [currentUnit?.id, partyId, t])

  useEffect(() => { void load() }, [load])

  const filtered = useMemo(() => {
    const query = searchQuery.trim().toLocaleLowerCase(locale)
    if (!query) return products
    return products.filter((product) => [
      product.internal_code,
      product.name,
      product.sped_item_type_code,
      product.item_purpose_key ? t(`accounting.fiscalMemory.itemTypes.${product.item_purpose_key}`) : null,
      product.source_document_name,
    ].some((value) => value?.toLocaleLowerCase(locale).includes(query)))
  }, [locale, products, searchQuery, t])
  const periodSummaries = useMemo(() => {
    const grouped = new Map<string, FiscalMemoryImport[]>()
    imports.forEach((memoryImport) => {
      const key = memoryImport.period_start && memoryImport.period_end
        ? `${memoryImport.period_start}|${memoryImport.period_end}`
        : memoryImport.id
      grouped.set(key, [...(grouped.get(key) || []), memoryImport])
    })
    return Array.from(grouped.entries()).map(([key, entries]): FiscalMemoryPeriodSummary => {
      const fiscal = entries.find((item) => item.source_kind === 'sped_fiscal') || null
      const contributions = entries.find((item) => item.source_kind === 'sped_contributions') || null
      const primary = fiscal || contributions || entries[0]
      const statuses = entries.map((item) => item.xml_enrichment_status)
      const xmlStatus = statuses.includes('pending')
        ? 'pending' as const
        : statuses.includes('completed_with_warnings')
          ? 'completed_with_warnings' as const
          : 'completed' as const
      return {
        key,
        period_start: primary.period_start,
        period_end: primary.period_end,
        imports: entries,
        primary,
        fiscal,
        contributions,
        product_count: fiscal?.product_count || Math.max(...entries.map((item) => item.product_count), 0),
        establishment_activity_kind: fiscal?.establishment_activity_kind
          || contributions?.establishment_activity_kind
          || null,
        xml_enrichment_status: xmlStatus,
        xml_expected_count: Math.max(...entries.map((item) => item.xml_expected_count), 0),
        xml_matched_count: Math.max(...entries.map((item) => item.xml_matched_count), 0),
        xml_missing_count: Math.max(...entries.map((item) => item.xml_missing_count), 0),
      }
    }).sort((left, right) => String(right.period_end || '').localeCompare(String(left.period_end || '')))
  }, [imports])
  const filteredImports = useMemo(() => {
    const query = searchQuery.trim().toLocaleLowerCase(locale)
    if (!query) return periodSummaries
    return periodSummaries.filter((period) => [
      ...period.imports.map((memoryImport) => memoryImport.source_document_name),
      period.period_start,
      period.period_end,
      period.establishment_activity_kind
        ? t(`accounting.fiscalMemory.activity.types.${period.establishment_activity_kind}`)
        : null,
    ].some((value) => value?.toLocaleLowerCase(locale).includes(query)))
  }, [locale, periodSummaries, searchQuery, t])
  const deletePeriodIcon = <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M6.75 7.5h10.5m-9 0 .6 11.1a1.5 1.5 0 0 0 1.5 1.4h3.3a1.5 1.5 0 0 0 1.5-1.4l.6-11.1M9.75 7.5V5.25A1.25 1.25 0 0 1 11 4h2a1.25 1.25 0 0 1 1.25 1.25V7.5" /></svg>
  const periodActions = (item: FiscalMemoryPeriodSummary): OverflowMenuItem[] => [{
    label: t('accounting.fiscalMemory.clear.periodAction'),
    icon: deletePeriodIcon,
    danger: true,
    onClick: () => setClearPeriodImport(item.primary),
  }]

  const formatDate = (value: string | null) => value
    ? new Intl.DateTimeFormat(locale, { dateStyle: 'short' }).format(new Date(`${value.slice(0, 10)}T12:00:00`))
    : '—'
  const typeLabel = (product: AccountingProduct) => product.sped_item_type_code
    ? `${product.sped_item_type_code} · ${t(`accounting.fiscalMemory.itemTypes.${product.item_purpose_key || 'unknown'}`)}`
    : t('accounting.fiscalMemory.itemTypes.unknown')
  const confidenceLabel = (product: AccountingProduct) => t('accounting.fiscalMemory.confidence.summary', {
    percent: String(product.classification_confidence),
    count: String(product.classification_observations),
  })
  const confidenceClass = (product: AccountingProduct) => product.classification_status === 'strong'
    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
    : product.classification_status === 'review'
      ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
      : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
  const columns: DataTableColumn<AccountingProduct>[] = [
    { key: 'code', label: t('accounting.products.columns.code'), render: (product) => <span className="font-semibold text-slate-900 dark:text-white">{product.internal_code || '—'}</span> },
    { key: 'description', label: t('accounting.products.columns.description'), render: (product) => <span className="block max-w-md truncate text-slate-700 dark:text-slate-200" title={product.name}>{product.name}</span> },
    { key: 'itemType', label: t('accounting.products.columns.itemType'), render: (product) => <span className="text-slate-700 dark:text-slate-200">{typeLabel(product)}</span> },
    { key: 'source', label: t('accounting.products.columns.source'), render: (product) => <span className="text-slate-700 dark:text-slate-200">{t(`accounting.fiscalMemory.sources.${product.source_kind}`)}</span> },
    { key: 'period', label: t('accounting.fiscalMemory.columns.period'), render: (product) => <span className="whitespace-nowrap text-slate-700 dark:text-slate-200">{formatDate(product.source_period_end)}</span> },
    {
      key: 'confidence',
      label: t('accounting.fiscalMemory.columns.confidence'),
      render: (product) => (
        <div className="flex flex-col items-start gap-1">
          <span
            className={`inline-flex flex-col rounded-lg px-2.5 py-1 text-xs font-semibold ${confidenceClass(product)}`}
            title={product.automatic_eligible ? t('accounting.fiscalMemory.confidence.eligible') : undefined}
          >
            <span>{t(`accounting.fiscalMemory.confidence.${product.classification_status}`)}</span>
            <span className="font-normal opacity-80">{confidenceLabel(product)}</span>
          </span>
          {product.memory_stage === 'catalog' && (
            <span className="text-xs font-medium text-amber-700 dark:text-amber-300">
              {t('accounting.fiscalMemory.confidence.xmlPending')}
            </span>
          )}
        </div>
      ),
    },
  ]
  const importColumns: DataTableColumn<FiscalMemoryPeriodSummary>[] = [
    { key: 'period', label: t('accounting.fiscalMemory.columns.period'), render: (item) => <span className="whitespace-nowrap font-semibold text-slate-900 dark:text-white">{formatDate(item.period_start)} – {formatDate(item.period_end)}</span> },
    { key: 'source', label: t('accounting.products.columns.source'), render: (item) => <div className="flex flex-wrap gap-1.5">{item.imports.map((source) => <span key={source.id} title={source.source_document_name} className="rounded-lg bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700 dark:bg-slate-700 dark:text-slate-200">{t(`accounting.fiscalMemory.sources.${source.source_kind}`)}</span>)}</div> },
    { key: 'activity', label: t('accounting.clients.form.activity'), render: (item) => <span className="text-slate-700 dark:text-slate-200">{item.establishment_activity_kind ? t(`accounting.fiscalMemory.activity.types.${item.establishment_activity_kind}`) : '—'}</span> },
    { key: 'xml', label: t('accounting.fiscalMemory.summary.xmlProgress'), render: (item) => <div><p className="font-medium text-slate-800 dark:text-slate-200">{t('accounting.fiscalMemory.xmlProgress', { matched: String(item.xml_matched_count), expected: String(item.xml_expected_count), missing: String(item.xml_missing_count) })}</p><span className={`mt-1 inline-flex rounded-full px-2 py-1 text-xs font-semibold ${item.xml_enrichment_status === 'completed' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : item.xml_enrichment_status === 'completed_with_warnings' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'}`}>{t(`accounting.fiscalMemory.xmlStatus.${item.xml_enrichment_status}`)}</span></div> },
    { key: 'action', label: '', align: 'right', render: (item) => <div className="flex items-center justify-end gap-1"><button type="button" onClick={() => setXmlImports(item.imports)} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:border-emerald-400 hover:bg-emerald-50 dark:border-slate-700 dark:text-emerald-300">{t(item.xml_enrichment_status === 'pending' ? 'accounting.fiscalMemory.linkXml' : 'accounting.fiscalMemory.addXml')}</button><OverflowMenu items={periodActions(item)} /></div> },
  ]
  const classified = products.filter((product) => Boolean(product.item_purpose_key)).length
  const classifiedPercent = products.length > 0 ? Math.round((classified / products.length) * 100) : 0
  const enriched = products.filter((product) => product.memory_stage === 'enriched').length
  const latestActivity = imports.find((memoryImport) => memoryImport.establishment_activity_kind)?.establishment_activity_kind || null
  const activityReview = latestActivity && latestActivity !== 'industrial'
    ? products.filter((product) => product.item_purpose_key === 'raw_material').length
    : 0

  return (
    <>
      <ListPageLayout
        title={t('accounting.fiscalMemory.title')}
        description={`${client?.name || ''} · ${t('accounting.fiscalMemory.total', { count: String(products.length) })}`}
        icon={<MemoryIcon />}
        backAction={{ label: t('accounting.fiscalMemory.back'), onClick: () => navigate('/accounting') }}
        onNew={client ? () => setShowImport(true) : undefined}
        newButtonLabel={t('accounting.fiscalMemory.newMemory')}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder={t(memoryView === 'individual' ? 'accounting.fiscalMemory.search' : 'accounting.fiscalMemory.searchImports')}
        clearSearchLabel={t('list.clearSearch')}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        onMobileViewOverride={setViewMode}
        showViewToggle
        loading={loading}
        error={error}
        headerActions={imports.length > 0 ? <OverflowMenu items={[{ label: t('accounting.fiscalMemory.clear.action'), icon: deletePeriodIcon, danger: true, onClick: () => setShowClearMemory(true) }]} /> : undefined}
        stickyToolbar={!loading ? <div className="flex flex-wrap items-center justify-between gap-3 px-1"><div className="flex gap-2">{(['individual', 'summary'] as const).map((item) => <button key={item} type="button" onClick={() => { setMemoryView(item); setSearchQuery('') }} className={`rounded-full px-3 py-1.5 text-xs font-semibold ${memoryView === item ? 'bg-emerald-600 text-white' : 'border border-slate-300 text-slate-600 dark:border-slate-700 dark:text-slate-300'}`}>{t(`accounting.fiscalMemory.views.${item}`)}</button>)}</div><p className="text-sm text-slate-600 dark:text-slate-400">{memoryView === 'individual' ? t('accounting.fiscalMemory.showing', { shown: String(filtered.length), total: String(products.length) }) : t('accounting.fiscalMemory.showingImports', { shown: String(filteredImports.length), total: String(periodSummaries.length) })}</p></div> : undefined}
      >
        {!loading && (
          <div className="space-y-6">
            {memoryView === 'summary' && <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <article className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900"><p className="text-xs text-slate-500">{t('accounting.fiscalMemory.summary.products')}</p><p className="mt-1 text-2xl font-semibold text-slate-900 dark:text-white">{products.length}</p></article>
              <article className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900"><p className="text-xs text-slate-500">{t('accounting.fiscalMemory.summary.classified')}</p><p className="mt-1 text-2xl font-semibold text-emerald-700 dark:text-emerald-400">{classified} · {classifiedPercent}%</p></article>
              <article className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900"><p className="text-xs text-slate-500">{t('accounting.fiscalMemory.summary.imports')}</p><p className="mt-1 text-2xl font-semibold text-slate-900 dark:text-white">{periodSummaries.length}</p></article>
              <article className="rounded-2xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-900 dark:bg-amber-950/20"><p className="text-xs text-amber-700 dark:text-amber-300">{t('accounting.fiscalMemory.summary.enriched')}</p><p className="mt-1 text-2xl font-semibold text-amber-800 dark:text-amber-200">{enriched} / {products.length}</p></article>
            </section>}

            {memoryView === 'summary' && activityReview > 0 && (
              <section className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950/20 dark:text-amber-200">
                <p className="font-semibold">{t('accounting.fiscalMemory.activity.reviewTitle', { count: String(activityReview) })}</p>
                <p className="mt-1 text-xs">{t('accounting.fiscalMemory.activity.reviewHint', { activity: t(`accounting.fiscalMemory.activity.types.${latestActivity}`) })}</p>
              </section>
            )}

            {memoryView === 'summary' ? (
              filteredImports.length === 0 ? (
                <section className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center dark:border-slate-700 dark:bg-slate-900">
                  <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">{t('accounting.fiscalMemory.summaryEmptyTitle')}</h2>
                  <p className="mx-auto mt-2 max-w-xl text-sm text-slate-500 dark:text-slate-400">{t('accounting.fiscalMemory.emptyDescription')}</p>
                </section>
              ) : viewMode === 'table' ? (
                <DataTable data={filteredImports} columns={importColumns} keyExtractor={(item) => item.key} />
              ) : (
                <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                  {filteredImports.map((item) => (
                    <article key={item.key} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
                      <div className="flex items-start justify-between gap-3 border-b border-slate-100 p-5 dark:border-slate-800"><div className="min-w-0"><h2 className="font-semibold text-slate-900 dark:text-white">{formatDate(item.period_start)} – {formatDate(item.period_end)}</h2><div className="mt-2 flex flex-wrap gap-1.5">{item.imports.map((source) => <span key={source.id} title={source.source_document_name} className="rounded-lg bg-slate-100 px-2 py-1 text-[11px] font-semibold text-slate-600 dark:bg-slate-700 dark:text-slate-200">{t(`accounting.fiscalMemory.sources.${source.source_kind}`)}</span>)}</div></div><div className="flex shrink-0 items-start gap-1"><div className="flex flex-col items-end gap-2"><span className={`rounded-full px-2 py-1 text-xs font-semibold ${item.xml_enrichment_status === 'completed' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : item.xml_enrichment_status === 'completed_with_warnings' ? 'bg-amber-100 text-amber-700 dark:bg-amber-950/20 dark:text-amber-300' : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'}`}>{t(`accounting.fiscalMemory.xmlStatus.${item.xml_enrichment_status}`)}</span><button type="button" onClick={() => setXmlImports(item.imports)} className="text-xs font-semibold text-emerald-700 hover:text-emerald-800 dark:text-emerald-300">{t(item.xml_enrichment_status === 'pending' ? 'accounting.fiscalMemory.linkXml' : 'accounting.fiscalMemory.addXml')}</button></div><OverflowMenu items={periodActions(item)} /></div></div>
                      <dl className="grid grid-cols-2 gap-4 p-5 text-sm"><div><dt className="text-xs text-slate-500">{t('accounting.fiscalMemory.summary.products')}</dt><dd className="mt-1 font-semibold text-slate-800 dark:text-slate-200">{item.product_count}</dd></div><div><dt className="text-xs text-slate-500">{t('accounting.clients.form.activity')}</dt><dd className="mt-1 font-semibold text-slate-800 dark:text-slate-200">{item.establishment_activity_kind ? t(`accounting.fiscalMemory.activity.types.${item.establishment_activity_kind}`) : '—'}</dd></div><div className="col-span-2"><dt className="text-xs text-slate-500">{t('accounting.fiscalMemory.summary.xmlProgress')}</dt><dd className="mt-1 font-semibold text-slate-800 dark:text-slate-200">{t('accounting.fiscalMemory.xmlProgress', { matched: String(item.xml_matched_count), expected: String(item.xml_expected_count), missing: String(item.xml_missing_count) })}</dd></div></dl>
                    </article>
                  ))}
                </section>
              )
            ) : filtered.length === 0 ? (
              <section className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center dark:border-slate-700 dark:bg-slate-900">
                <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">{t('accounting.fiscalMemory.emptyTitle')}</h2>
                <p className="mx-auto mt-2 max-w-xl text-sm text-slate-500 dark:text-slate-400">{t('accounting.fiscalMemory.emptyDescription')}</p>
                <button type="button" onClick={() => setShowImport(true)} className="mt-5 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700">{t('accounting.fiscalMemory.linkSped')}</button>
              </section>
            ) : viewMode === 'table' ? (
              <DataTable data={filtered} columns={columns} keyExtractor={(product) => product.asset_id} onRowClick={setSelectedProduct} />
            ) : (
              <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {filtered.map((product) => (
                  <article
                    key={product.asset_id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedProduct(product)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        setSelectedProduct(product)
                      }
                    }}
                    className="cursor-pointer overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:border-emerald-400 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:border-slate-700 dark:bg-slate-900"
                  >
                    <div className="flex items-start justify-between gap-3 border-b border-slate-100 p-5 dark:border-slate-800">
                      <div className="min-w-0">
                        <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-400">{product.internal_code || '—'}</p>
                        <h2 className="mt-1 truncate font-semibold text-slate-900 dark:text-white" title={product.name}>{product.name}</h2>
                      </div>
                      <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${confidenceClass(product)}`}>
                        {t(`accounting.fiscalMemory.confidence.${product.classification_status}`)}
                      </span>
                    </div>
                    <dl className="grid grid-cols-2 gap-4 p-5 text-sm">
                      <div className="col-span-2">
                        <dt className="text-xs text-slate-500 dark:text-slate-400">{t('accounting.products.columns.itemType')}</dt>
                        <dd className="mt-1 font-medium text-slate-800 dark:text-slate-200">{typeLabel(product)}</dd>
                      </div>
                      <div>
                        <dt className="text-xs text-slate-500 dark:text-slate-400">{t('accounting.products.columns.source')}</dt>
                        <dd className="mt-1 text-slate-700 dark:text-slate-200">{t(`accounting.fiscalMemory.sources.${product.source_kind}`)}</dd>
                      </div>
                      <div>
                        <dt className="text-xs text-slate-500 dark:text-slate-400">{t('accounting.fiscalMemory.columns.period')}</dt>
                        <dd className="mt-1 text-slate-700 dark:text-slate-200">{formatDate(product.source_period_end)}</dd>
                      </div>
                    </dl>
                    <div className="flex items-center justify-between gap-3 border-t border-slate-100 px-5 py-3 text-xs dark:border-slate-800">
                      <span className="text-slate-500 dark:text-slate-400">{confidenceLabel(product)}</span>
                      {product.memory_stage === 'catalog' && (
                        <span className="font-semibold text-amber-700 dark:text-amber-300">{t('accounting.fiscalMemory.confidence.xmlPending')}</span>
                      )}
                    </div>
                  </article>
                ))}
              </section>
            )}
          </div>
        )}
      </ListPageLayout>

      <FiscalMemoryImportModal
        client={client}
        currentProducts={products}
        isOpen={showImport}
        onClose={() => setShowImport(false)}
        onImported={() => void load()}
      />
      <FiscalMemoryXmlLinkModal
        memoryImports={xmlImports}
        products={products}
        spedDocuments={documents}
        isOpen={xmlImports.length > 0}
        onClose={() => setXmlImports([])}
        onLinked={() => void load()}
      />
      <FiscalMemoryDetailsModal
        product={selectedProduct}
        isOpen={Boolean(selectedProduct)}
        onClose={() => setSelectedProduct(null)}
      />
      <FiscalMemoryClearModal
        unitId={client?.client_unit_id || null}
        clientName={client?.name || ''}
        imports={imports}
        isOpen={showClearMemory}
        onClose={() => setShowClearMemory(false)}
        onCleared={() => void load()}
      />
      <FiscalMemoryClearModal
        unitId={client?.client_unit_id || null}
        clientName={client?.name || ''}
        imports={imports}
        presetImportId={clearPeriodImport?.id || null}
        periodOnly
        isOpen={Boolean(clearPeriodImport)}
        onClose={() => setClearPeriodImport(null)}
        onCleared={() => void load()}
      />
    </>
  )
}
